import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import { Row, Col } from 'react-flexbox-grid';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import {
  debtorRegistrationActions,
  debtorRegistrationActionsT,
  creditorRegistrationActions,
  loanActions,
  accountDocumentTypes,
} from '../../../common/enums';
import DocumentList from './DocumentList';
import { ContractsList } from './../../account-management/components/contracts';
import BiometricVerification from './BiometricVerificationIFrame';
import Psd2BankConnect from '../../psd2/components/Psd2BankConnect';
import { t } from '../../../app/i18n/index';

const confirmEmailActions = [
  creditorRegistrationActions.CONFIRM_EMAIL,
];

const uploadDocumentsActions = [
  debtorRegistrationActions.UPLOAD_DOCUMENTS,
  creditorRegistrationActions.UPLOAD_DOCUMENTS,
  debtorRegistrationActions.UPLOAD_BANK_ACCOUNT_STATEMENTS,
  loanActions.UPLOAD_BANK_ACCOUNT_STATEMENTS,
];

const waitForConfirmationActions = [
  debtorRegistrationActions.WAIT_FOR_DOCUMENT_CONFIRMATION,
  creditorRegistrationActions.WAIT_FOR_DOCUMENT_CONFIRMATION,
];

const getStatusInfo = (registrationAction, t) => {
  if (uploadDocumentsActions.includes(registrationAction)) {
    return t('Dokumenty potrebné na nahratie');
  } else if (waitForConfirmationActions.includes(registrationAction)) {
    return t('Dokumenty čakajúce na schválenie');
  } else if (registrationAction === creditorRegistrationActions.UPDATE_PROFILE) {
    return t('Prosím dokončite registráciu');
  } else if (confirmEmailActions.includes(registrationAction)) {
    return t('Prosím overte Vašu emailovú adresu pred nahratím dokumentov');
  } else {
    return t('Schválené dokumenty');
  }
};

@translate()
export default class DocumentManagement extends Component {
  static propTypes = {
    allowedMimeTypes: PropTypes.array.isRequired,
    documents: PropTypes.array.isRequired,
    generalContract: PropTypes.array,
    accountRole: PropTypes.string.isRequired,
    isSubmitDisabled: PropTypes.bool.isRequired,
    isUpdating: PropTypes.bool.isRequired,
    isUploading: PropTypes.bool.isRequired,
    onOptionalDocumentChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onUpload: PropTypes.func.isRequired,
    optionalDocumentType: PropTypes.string.isRequired,
    registrationAction: PropTypes.string,
    t: PropTypes.func.isRequired,
    token: PropTypes.string.isRequired,
    saveVerificationResult: PropTypes.func.isRequired,
    documentsFetched: PropTypes.bool.isRequired,
    useBiometrics: PropTypes.bool.isRequired,
    isSavingVerificationResult: PropTypes.bool.isRequired,

    // NEW optional props for PSD2 flow
    psd2Enabled: PropTypes.bool,
    psd2Discount: PropTypes.number,
    psd2LastResult: PropTypes.oneOf(['idle', 'success', 'error']),
    psd2BanksMain: PropTypes.array,
    psd2BanksOther: PropTypes.array,
    onStartPsd2: PropTypes.func,
    onOpenManualUpload: PropTypes.func,

    bankStatementsLocked: PropTypes.bool,
    bankStatementTypes: PropTypes.array,

    showAdditionalStatementsLink: PropTypes.bool,
    shouldShowPsd2Step: PropTypes.bool,
    shouldPreProceedPsd2: PropTypes.bool,
    psd2ImportedAccounts: PropTypes.array,
  };

  static defaultProps = {
    psd2Enabled: true,
    psd2LastResult: 'idle',
    psd2BanksMain: [],
    psd2BanksOther: [],
    bankStatementsLocked: false,
    bankStatementTypes: [],
    showAdditionalStatementsLink: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      iframeClosed: false,
      proceededAfterPsd2: props.shouldPreProceedPsd2,
    };
  }

  closeIframeModal = () => this.setState({ iframeClosed: true });

  isBankStatementsStage = (action) => {
    return (
      action === debtorRegistrationActions.UPLOAD_BANK_ACCOUNT_STATEMENTS ||
      action === loanActions.UPLOAD_BANK_ACCOUNT_STATEMENTS
    );
  };

  isIdUploadStage = (action) => {
    return (action === debtorRegistrationActions.UPLOAD_DOCUMENTS || action === debtorRegistrationActions.WAIT_FOR_DOCUMENT_CONFIRMATION)
  }

  handleSkipToManual = () => {
    if (this.props.onOpenManualUpload) {
      this.props.onOpenManualUpload();
    } else {
      this.setState({ proceededAfterPsd2: true });
    }
  };

  handleContinueAfterSuccess = () => {
    this.setState({ proceededAfterPsd2: true });
  };

  handleAddMorePsd2 = () => {
    this.setState({proceededAfterPsd2: false});
  }

  renderDescription(isPSD2) {
    const message = isPSD2
     ? debtorRegistrationActionsT['UPLOAD_PSD2_BANK_ACCOUNT_STATEMENTS']
      : debtorRegistrationActionsT['UPLOAD_BANK_ACCOUNT_STATEMENTS']
    const discountMessage = this.props.psd2Discount > 0 ? t('Pri využití online overenia bankového účtu, získate zľavu na úrokovej sadzbe {{discount}}%',{discount:this.props.psd2Discount}) : null;
    return (
      <div>
        <div className="registration-action-alert">
          <p>{message}</p>
        </div>
        {discountMessage
         ? <div className="registration-action-alert">
            <p style={{fontWeight:'bold'}}>{discountMessage}</p>
          </div>
         : null}
        {!isPSD2 && (
          <div className="registration-action-alert">
            <p style={{ marginTop: 8 }}>
              {'Po nahratí a schválení dokladov budete môcť podpísať Informačné povinnosti poskytovateľa služieb, čo je nevyhnutný krok pred podpisom Záväznej žiadosti o pôžičku.'}
            </p>
          </div>
        )}
      </div>
    );
  }

  renderHeaderExtras = () => {
    const { t, showAdditionalStatementsLink, bankStatementsLocked } = this.props;

    if (!showAdditionalStatementsLink || !this.state.proceededAfterPsd2) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <Button size="small" onClick={this.handleAddMorePsd2}>
          {bankStatementsLocked ? t('Ak máte ďalšie bankové účty, doplňte ich.') : t('Využiť online autorizované overenie účtu')}
        </Button>
      </div>
    );
  };

  render() {
    const {
      allowedMimeTypes,
      documents,
      generalContract,
      isUpdating,
      isUploading,
      isSubmitDisabled,
      onOptionalDocumentChange,
      onRemove,
      onSubmit,
      onUpload,
      optionalDocumentType,
      registrationAction,
      t,
      token,
      accountRole,
      saveVerificationResult,
      documentsFetched,
      useBiometrics,
      isSavingVerificationResult,
      psd2Enabled,
      psd2LastResult,
      psd2BanksMain,
      psd2BanksOther,
      onStartPsd2,
      bankStatementsLocked,
      bankStatementTypes,
      shouldShowPsd2Step,
    } = this.props;

    const missingDocuments = documents
      .filter(doc => doc.documentType == accountDocumentTypes.ID_CARD_SCAN_FRONT || doc.documentType == accountDocumentTypes.ID_CARD_SCAN_BACK)
      .some(doc => !(doc.file.id));

    const uploadingAvailable = uploadDocumentsActions.includes(registrationAction);
    const atBankStatementsStage = this.isBankStatementsStage(registrationAction);
    const atUploadIdStage = this.isIdUploadStage(registrationAction)
    const statusInfo = getStatusInfo(registrationAction, t);
    const showBiometricVerification = useBiometrics && documentsFetched && missingDocuments && !this.state.iframeClosed;

    const shouldShowPsd2 = psd2Enabled && shouldShowPsd2Step && !this.state.proceededAfterPsd2 && !atUploadIdStage;

    return (
      <section className="debtor-document-management">
        {!atUploadIdStage && atBankStatementsStage ? (<Row around="xs">
          <Col xs={12}>
            <Alert
              message={this.renderDescription(shouldShowPsd2)}
              type="warning"
              showIcon
              description={'\t'}
            />
          </Col>
        </Row>) : null}
        {showBiometricVerification ? (
          <BiometricVerification
            onClose={this.closeIframeModal}
            saveVerificationResult={saveVerificationResult}
          />
        ) : null}

        {!shouldShowPsd2 && (<h2>{statusInfo}</h2>)}
        {this.renderHeaderExtras()}

        {shouldShowPsd2 ? (
          <Psd2BankConnect
            banksMain={psd2BanksMain}
            banksOther={psd2BanksOther}
            lastResult={psd2LastResult}
            isStarting={isUploading || isUpdating}
            onStartPsd2={onStartPsd2}
            onSkipToManual={this.handleSkipToManual}
            onContinueAfterSuccess={this.handleContinueAfterSuccess}
            importedAccounts={this.props.psd2ImportedAccounts}
          />
        ) : (
          <div>
            <Row>
              <Col xs={12}>
                <DocumentList
                  allowedMimeTypes={allowedMimeTypes}
                  documents={documents}
                  loading={isUpdating || isUploading || isSavingVerificationResult}
                  onOptionalDocumentChange={onOptionalDocumentChange}
                  onRemove={onRemove}
                  onUpload={onUpload}
                  optionalDocumentType={optionalDocumentType}
                  token={token}
                  uploadingAvailable={uploadingAvailable}
                  bankStatementsLocked={bankStatementsLocked}
                  bankStatementTypes={bankStatementTypes}
                />
              </Col>
            </Row>

            {uploadingAvailable && (
              <Row className="row-margin-top align-items-center upload-panel">
                <Col xs={12} sm={9} />
                <Col xs={12} sm={3}>
                  <Button type="danger" onClick={onSubmit} disabled={isSubmitDisabled}>
                    {t('Odoslať na schválenie')}
                  </Button>
                </Col>
              </Row>
            )}

            {(generalContract && generalContract.length > 0) && (
              <div>
                <h2 style={{ marginTop: 30 }}>{t('Vygenerované zmluvy')}</h2>
                <Row>
                  <Col xs={12}>
                    <ContractsList
                      token={token}
                      documents={generalContract}
                      accountRole={accountRole}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </div>
        )}
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/documents/components/DocumentManagement.js